import { z } from "zod"

/**
 * Schema for creating/editing water quality tests.
 * Validation rules match backend constraints.
 */
export const waterQualitySchema = z.object({
  // Required fields
  dissolvedOxygen: z
    .number({ message: "Dissolved oxygen is required" })
    .min(0, "Must be at least 0")
    .max(100, "Must be at most 100 mg/L"),

  ph: z
    .number({ message: "pH is required" })
    .min(0, "Must be at least 0")
    .max(14, "Must be at most 14"),

  temperature: z
    .number({ message: "Temperature is required" })
    .min(0, "Must be at least 0")
    .max(50, "Must be at most 50°C"),

  testedAt: z.date({ message: "Test date/time is required" }),

  // Optional fields
  salinity: z
    .number()
    .min(0, "Must be at least 0")
    .max(50, "Must be at most 50 ppt")
    .optional()
    .nullable(),

  ammonia: z
    .number()
    .min(0, "Must be at least 0")
    .max(100, "Must be at most 100 mg/L")
    .optional()
    .nullable(),

  nitrite: z
    .number()
    .min(0, "Must be at least 0")
    .max(100, "Must be at most 100 mg/L")
    .optional()
    .nullable(),

  nitrate: z
    .number()
    .min(0, "Must be at least 0")
    .max(100, "Must be at most 100 mg/L")
    .optional()
    .nullable(),

  turbidity: z
    .number()
    .min(0, "Must be at least 0")
    .max(1000, "Must be at most 1000 NTU")
    .optional()
    .nullable(),

  notes: z.string().max(2000, "Notes must be less than 2000 characters").optional().nullable(),

  imageIds: z.array(z.string()).max(5, "Maximum 5 images allowed").optional(),
})

export type WaterQualityFormData = z.infer<typeof waterQualitySchema>

/**
 * Convert form data to API request format.
 * Converts Date to ISO string and filters out null/undefined optional fields.
 */
export function toApiRequest(data: WaterQualityFormData) {
  return {
    dissolvedOxygen: data.dissolvedOxygen,
    ph: data.ph,
    temperature: data.temperature,
    testedAt: data.testedAt.toISOString(),
    ...(data.salinity != null && { salinity: data.salinity }),
    ...(data.ammonia != null && { ammonia: data.ammonia }),
    ...(data.nitrite != null && { nitrite: data.nitrite }),
    ...(data.nitrate != null && { nitrate: data.nitrate }),
    ...(data.turbidity != null && { turbidity: data.turbidity }),
    ...(data.notes && { notes: data.notes }),
    ...(data.imageIds && data.imageIds.length > 0 && { imageIds: data.imageIds }),
  }
}
